import { ref } from 'vue';

import { ElNotification } from 'element-plus';

import {
  acceptedInviteCodes,
  clearStoredInviteCode,
  getInviteErrorCode,
  getStoredInviteCode,
  getStoredInviteEmail,
  INVITE_EMAIL_MISMATCH_CODE,
  isInviteEmailMismatch,
} from '@/helpers/inviteCode';
import i18n from '@/plugins/i18n';
import { session } from '@/services/auth/session';
import { useInvitesStore } from '@/stores/invites';
import { trackEvent } from '@/utils/ga';

let activeAcceptance = null;

// Set when the pending invite belongs to a different email than the signed-in user (found before
// the call, or reported by the backend as invite_email_mismatch). The register page shows its
// sign-out-or-cancel prompt from it; the code stays stored so the right account can still accept.
export const inviteMismatch = ref(null);
export const clearInviteMismatch = () => {
  inviteMismatch.value = null;
};

const MISMATCH_MESSAGE =
  'This invitation was sent to a different email address than the one you are signed in with.';

const flagMismatch = () => {
  inviteMismatch.value = {
    currentEmail: session.user.value?.email ?? '',
    invitedEmail: getStoredInviteEmail() ?? '',
  };
  // The register page explains it with its own prompt; elsewhere (login, oauth-landing) a toast.
  if (!window.location.pathname.startsWith('/register')) {
    ElNotification({ type: 'warning', message: i18n.global.t(MISMATCH_MESSAGE), duration: 6000 });
  }
};

// Failed acceptance must remain retryable and must never turn an invited signup
// into ordinary signup. Background restoration can continue; onboarding requires success.
export async function acceptPendingInvite({ required = false } = {}) {
  const code = getStoredInviteCode();
  if (!code || !session.isAuthenticated.value) return false;

  // An invite meant for someone else must never reach the backend: a mismatched accept used to
  // answer 401 and cascade into a forced logout. Keep the code for the right account.
  if (isInviteEmailMismatch(session.user.value?.email, getStoredInviteEmail())) {
    flagMismatch();
    if (required) throw new Error(i18n.global.t(MISMATCH_MESSAGE));
    return false;
  }

  const subject = session.user.value?.sub;
  if (!activeAcceptance || activeAcceptance.code !== code || activeAcceptance.subject !== subject) {
    activeAcceptance = { code, subject, promise: accept(code) };
  }
  const attempt = activeAcceptance;
  try {
    const accepted = await attempt.promise;
    if (!accepted && required) {
      throw new Error(i18n.global.t('Your invitation could not be accepted. Retry, or ask the inviter for a new invitation.'));
    }
    return accepted;
  } finally {
    if (activeAcceptance === attempt) activeAcceptance = null;
  }
}

async function accept(code) {
  const invites = useInvitesStore();
  try {
    await invites.acceptInvite({ code });
  } catch (error) {
    if (getInviteErrorCode(error) === INVITE_EMAIL_MISMATCH_CODE) {
      flagMismatch();
      return false;
    }
    ElNotification({
      type: 'warning',
      message: i18n.global.t('Your invitation could not be accepted. Retry, or ask the inviter for a new invitation.'),
      duration: 5000,
    });
    console.warn('[invite] accept failed:', error?.response?.status ?? 'network');
    return false;
  }
  // The accept is not followed by a /users/me, which would still list this invite as SENT and keep
  // the banner showing it. Drop it locally rather than session.restore(): that re-runs the whole
  // bootstrap restore (initializing flag, default-profile re-scope) in the middle of onboarding,
  // just before the switch-profile, and a failure there would turn a successful accept into an error.
  acceptedInviteCodes.value = [...acceptedInviteCodes.value, code];
  session.removeInvite(code);
  // Another tab may have opened a different invitation while this one was pending.
  if (getStoredInviteCode() === code) clearStoredInviteCode();
  clearInviteMismatch();
  trackEvent('invite_accept');
  ElNotification({ type: 'success', title: i18n.global.t('Invitation accepted') });
  return true;
}
